import { useEffect, useLayoutEffect, useState, type KeyboardEvent, type RefObject } from 'react';

// A panel that opens next to a table header: the column filter and the column
// chooser. The panel renders through a portal with `position: fixed`, because
// the table card clips its overflow. The top and left coordinates are the one
// inline style of such a panel, as in the `HoverPopover` overlay.

const VIEWPORT_MARGIN = 8;

export interface AnchoredPanelOptions {
    anchor: HTMLElement;
    panelRef: RefObject<HTMLDivElement | null>;
    width: number;
    /** `start` aligns the left edges, `end` aligns the right edges. */
    align?: 'start' | 'end';
    /** An attribute on the trigger. A pointer-down on the trigger does not
     *  close the panel, so the trigger itself can toggle it. */
    triggerAttribute: string;
    onClose: () => void;
}

/**
 * The position of the panel, and its close rules: a press outside the panel,
 * a scroll outside it, and a resize. An on-screen keyboard resizes the window
 * height and scrolls the page, so both are ignored while focus is inside the
 * panel. A width change still closes it.
 */
export function useAnchoredPanel({
    anchor,
    panelRef,
    width,
    align = 'start',
    triggerAttribute,
    onClose,
}: AnchoredPanelOptions): { top: number; left: number } {
    const [pos, setPos] = useState({ top: 0, left: 0 });

    // Runs after every render, because the height of the panel can change. The
    // state write is skipped when nothing moved, so this cannot loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useLayoutEffect(() => {
        const panel = panelRef.current;
        if (!panel) return;
        const rect = anchor.getBoundingClientRect();
        const height = panel.offsetHeight;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const wanted = align === 'end' ? rect.right - width : rect.left;
        const left = Math.max(VIEWPORT_MARGIN, Math.min(wanted, vw - width - VIEWPORT_MARGIN));
        let top = rect.bottom + 2;
        if (top + height > vh - VIEWPORT_MARGIN) {
            const above = rect.top - height - 2;
            top =
                above >= VIEWPORT_MARGIN
                    ? above
                    : Math.max(VIEWPORT_MARGIN, vh - height - VIEWPORT_MARGIN);
        }
        setPos((old) => (old.top === top && old.left === left ? old : { top, left }));
    });

    useEffect(() => {
        const onPointerDown = (e: PointerEvent) => {
            const target = e.target as Element | null;
            if (!target) return;
            if (panelRef.current?.contains(target)) return;
            if (target.closest(`[${triggerAttribute}]`)) return;
            onClose();
        };
        const focusInPanel = () => panelRef.current?.contains(document.activeElement) ?? false;
        const openWidth = window.innerWidth;
        const onScroll = (e: Event) => {
            if (e.target instanceof Node && panelRef.current?.contains(e.target)) return;
            if (focusInPanel()) return;
            onClose();
        };
        const onResize = () => {
            if (window.innerWidth === openWidth && focusInPanel()) return;
            onClose();
        };
        document.addEventListener('pointerdown', onPointerDown, true);
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onResize);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown, true);
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onResize);
        };
    }, [onClose, panelRef, triggerAttribute]);

    return pos;
}

/** Keeps Tab and Shift+Tab inside the panel. */
export function trapPanelTab(e: KeyboardEvent<HTMLElement>, panel: HTMLElement | null): void {
    if (e.key !== 'Tab' || !panel) return;
    const items = Array.from(panel.querySelectorAll<HTMLElement>('button, input, select')).filter(
        (el) => !(el as HTMLButtonElement).disabled && el.tabIndex >= 0,
    );
    if (items.length === 0) return;
    const firstItem = items[0];
    const lastItem = items[items.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === firstItem || !panel.contains(active))) {
        e.preventDefault();
        lastItem.focus();
    } else if (!e.shiftKey && (active === lastItem || !panel.contains(active))) {
        e.preventDefault();
        firstItem.focus();
    }
}
